import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CodeInput, TotpSetup, EmailEnroll, RecoveryCodesDialog } from "@/components/security";
import { ShieldCheck } from "lucide-react";
import { useBranding, BrandLogo } from "@/context/BrandingContext";

export function AuthLayout({ children, title, subtitle }) {
  const { brand } = useBranding();
  const name = brand?.identity.platform_name || "CASURECO II Management Portal";
  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr]">
      <div className="hidden lg:flex flex-col justify-between p-12 relative overflow-hidden app-sidebar">
        <div className="absolute inset-0 opacity-[0.07] grid-bg" style={{ filter: "invert(1)" }} />
        <div className="relative flex items-center gap-3">
          <BrandLogo kind="logo_dark" className="h-10 w-10" />
          <span className="font-display text-xl font-bold" style={{ color: "var(--c-header-text)" }}>{brand?.identity.short_name}</span>
        </div>
        <div className="relative max-w-md">
          <p className="text-xs uppercase tracking-[0.2em] mb-4" style={{ color: "var(--c-sidebar-active-text)" }}>{brand?.identity.owner_name}</p>
          <h1 className="text-4xl xl:text-5xl font-extrabold leading-tight" style={{ color: "var(--c-header-text)" }}>One secure sign-in for staff and member-consumers.</h1>
          <p className="opacity-70 mt-5">{brand?.identity.description}</p>
        </div>
        <p className="relative text-xs opacity-60">{brand?.identity.copyright_text || "Protected by configurable 2FA · Email OTP · Authenticator App"}</p>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12 bg-slate-50">
        <div className="w-full max-w-md fade-up">
          <div className="flex items-center gap-3 mb-6" data-testid="login-brand">
            <BrandLogo kind="logo_login" className="h-12 w-12" />
            <p className="font-display font-bold text-lg leading-tight text-slate-900" data-testid="login-platform-name">{name}</p>
          </div>
          <h2 className="text-2xl font-bold text-slate-900">{title}</h2>
          {subtitle && <p className="text-sm text-slate-500 mt-1 mb-8">{subtitle}</p>}
          {children}
        </div>
      </div>
    </div>
  );
}

function OtpStep({ pending, onDone }) {
  const [method, setMethod] = useState(pending.preferred_method);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    try { await api.post("/auth/challenge/email", { pending_token: pending.pending_token }, { skipRefresh: true }); toast.success("Code sent to your email"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const verify = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post("/auth/challenge/verify", { pending_token: pending.pending_token, method, code }, { skipRefresh: true });
      onDone(data);
    } catch (ex) { toast.error(errMsg(ex)); } finally { setBusy(false); }
  };
  const methods = [...pending.methods, ...(pending.recovery_codes_allowed ? ["recovery"] : [])];
  return (
    <form onSubmit={verify} className="space-y-5" data-testid="otp-challenge-form">
      <div className="flex items-center gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-3">
        <ShieldCheck className="h-4 w-4" /> Two-Factor Authentication required to finish signing in.
      </div>
      <div className="flex gap-2 flex-wrap">
        {methods.map((m) => (
          <Button type="button" key={m} size="sm" variant={method === m ? "default" : "outline"} onClick={() => { setMethod(m); setCode(""); }} data-testid={`otp-method-${m}`}>
            {m === "email" ? "Email OTP" : m === "totp" ? "Authenticator App" : "Recovery code"}
          </Button>
        ))}
      </div>
      {method === "email" && <Button type="button" variant="secondary" onClick={send} data-testid="otp-send-email-button">Send code to my email</Button>}
      {method === "totp" && <p className="text-sm text-slate-500">Open your authenticator app and enter the current code.</p>}
      <CodeInput value={code} onChange={setCode} length={method === "recovery" ? 9 : 6} placeholder={method === "recovery" ? "XXXX-XXXX" : undefined} testId="otp-code-input" />
      <Button type="submit" className="w-full" disabled={busy || code.length < 6} data-testid="otp-verify-button">Verify & sign in</Button>
      <p className="text-xs text-slate-500">Lost access to both? <Link to="/recover" className="text-amber-700 underline">Request account recovery</Link></p>
    </form>
  );
}

function EnrollStep({ pending, onDone }) {
  const [method, setMethod] = useState(null);
  const tok = { pending_token: pending.pending_token };
  const cfg = { skipRefresh: true };
  return (
    <div className="space-y-5" data-testid="enrollment-required-panel">
      <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-3">{pending.message}</div>
      {!method && (
        <div className="grid gap-3">
          {pending.available_methods.includes("totp") && <Button variant="outline" className="justify-start h-auto py-3" onClick={() => setMethod("totp")} data-testid="enroll-choose-totp">Authenticator App (Google Authenticator)</Button>}
          {pending.available_methods.includes("email") && <Button variant="outline" className="justify-start h-auto py-3" onClick={() => setMethod("email")} data-testid="enroll-choose-email">Email OTP</Button>}
        </div>
      )}
      {method === "totp" && (
        <TotpSetup onCancel={() => setMethod(null)}
          start={async () => (await api.post("/auth/enroll/totp/start", tok, cfg)).data}
          confirm={async (code) => onDone((await api.post("/auth/enroll/totp/confirm", { ...tok, method: "totp", code }, cfg)).data)} />
      )}
      {method === "email" && (
        <EmailEnroll email="your registered email" onCancel={() => setMethod(null)}
          start={async () => (await api.post("/auth/enroll/email/start", tok, cfg)).data}
          confirm={async (step, code) => {
            const { data } = await api.post("/auth/enroll/email/confirm", { ...tok, step, code }, cfg);
            if (data.status === "authenticated") onDone(data);
            return data;
          }} />
      )}
    </div>
  );
}

export default function Login() {
  const { user, completeLogin } = useAuth();
  const { brand } = useBranding();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [pending, setPending] = useState(null);
  const [codes, setCodes] = useState(null);
  const [final, setFinal] = useState(null);
  const [busy, setBusy] = useState(false);
  if (user && !codes) return <Navigate to="/app" replace />;

  const finish = (data) => {
    if (data.recovery_codes?.length) { setFinal(data); setCodes(data.recovery_codes); return; }
    completeLogin(data);
    if (data.prompt_2fa_setup) toast.info("Protect your account: enable Two-Factor Authentication in Profile & Security.");
    nav("/app");
  };
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { ...form, client: "web" }, { skipRefresh: true });
      if (data.status === "authenticated") finish(data); else setPending(data);
    } catch (ex) { toast.error(errMsg(ex)); } finally { setBusy(false); }
  };
  const title = !pending ? "Sign in" : pending.status === "otp_required" ? "Verify it's you" : "Set up Two-Factor Authentication";
  return (
    <AuthLayout title={title} subtitle={!pending ? "Staff and registered member-consumers" : "Step 2 of 2"}>
      {!pending && (
        <form onSubmit={submit} className="space-y-4" data-testid="login-form">
          <div className="space-y-1.5"><Label>Email</Label>
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="login-email-input" required /></div>
          <div className="space-y-1.5"><Label>Password</Label>
            <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="login-password-input" required /></div>
          <Button type="submit" className="w-full" disabled={busy} data-testid="login-submit-button">Continue</Button>
          <div className="flex justify-between text-sm pt-2">
            {brand?.general?.self_registration_enabled !== false ? <Link to="/register" className="hover:underline" style={{ color: "var(--c-link)" }} data-testid="register-link">Register as a subscriber</Link> : <span />}
            <Link to="/recover" className="text-slate-500 hover:underline" data-testid="recover-link">Lost 2FA access?</Link>
          </div>
        </form>
      )}
      {pending?.status === "otp_required" && <OtpStep pending={pending} onDone={finish} />}
      {pending?.status === "enrollment_required" && <EnrollStep pending={pending} onDone={finish} />}
      {pending && <Button variant="link" className="px-0 mt-4 text-slate-500" onClick={() => setPending(null)} data-testid="login-back-button">← Start over</Button>}
      <RecoveryCodesDialog codes={codes} onClose={() => { setCodes(null); completeLogin(final); nav("/app"); }} />
    </AuthLayout>
  );
}
